import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { Box, CircularProgress } from '@mui/material';

/**
 * Este componente serve como um guarda para as rotas da sua aplicação.
 * Ele gerencia o acesso com base no estado de autenticação, permissões (roles)
 * e a lógica de negócio específica para administradores que precisam configurar uma organização.
 */
const ProtectedRoute = ({ allowedRoles = [] }) => {
    const location = useLocation();
    // Assumimos que o useAuth retorna o usuário, o token e um estado de carregamento.
    const { user, loading } = useAuth();

    console.log("ProtectedRoute: Render. User:", user, "Loading:", loading, "Path:", location.pathname);

    // 1. Exibe um loader enquanto a autenticação está sendo verificada.
    // Isso previne redirecionamentos incorretos antes que o estado de auth seja totalmente resolvido.
    if (loading) {
        console.log("ProtectedRoute: Autenticação em carregamento. Mostrando loader.");
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }
    
    // 2. Após o carregamento, se não houver usuário, redireciona para a página de login.
    // O `state={{ from: location }}` guarda a página que o usuário tentou acessar.
    if (!user) { 
        console.log("ProtectedRoute: Usuário não autenticado. Redirecionando para /login.");
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // 3. Lógica para Admin que ainda não configurou uma empresa.
    // Se for um 'admin' sem 'companyId', ele deve ser forçado a ir para a página de setup.
    if (user.role === 'admin' && !user.companyId) {
        // Apenas redireciona se ele NÃO estiver já na página de setup, para evitar um loop.
        if (location.pathname !== '/organizacao/setup') {
            console.log("ProtectedRoute: Admin sem companyId e não em setup. Redirecionando para /organizacao/setup.");
            return <Navigate to="/organizacao/setup" replace />;
        }
        // Se ele for admin sem empresa e já estiver na página de setup, permite o acesso.
        console.log("ProtectedRoute: Admin sem companyId, mas já está em /organizacao/setup. Permitindo acesso.");
        return <Outlet />; 
    }

    // 4. Se um admin que JÁ TEM uma empresa tentar acessar a página de setup novamente,
    // ele é redirecionado para a página inicial para evitar uma nova configuração.
    if (user.role === 'admin' && user.companyId && location.pathname === '/organizacao/setup') {
        console.log("ProtectedRoute: Admin com companyId em setup. Redirecionando para /inicial.");
        return <Navigate to="/inicial" replace />;
    }

    // 5. Verificação de permissões (roles) para rotas específicas.
    // Usado para rotas como as de 'superadmin'.
    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
        console.log(`ProtectedRoute: Acesso negado para role '${user.role}' na rota '${location.pathname}'. Redirecionando para /unauthorized.`);
        return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }

    // Se todas as verificações foram aprovadas, permite o acesso à rota solicitada.
    console.log("ProtectedRoute: Acesso permitido. Renderizando Outlet.");
    return <Outlet />;
};

export default ProtectedRoute;
